import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { api } from '../api';

interface ImageUploadProps {
  onImageSelect: (imageUrl: string) => void;
  currentImageUrl?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ onImageSelect, currentImageUrl }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Component mounted
  React.useEffect(() => {
    // Component is ready
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPEG, PNG, GIF, etc.)');
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('Image file is too large. Please select an image smaller than 5MB.');
      return;
    }

    setIsLoadingFile(true);
    
    try {
      // Store the file reference for later upload
      setSelectedFile(file);
      
      // Create a preview for display only
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      
      // Don't call onImageSelect yet - wait for upload
    } catch (error) {
      console.error('Error creating preview:', error);
      alert('Failed to load image. Please try a different file.');
    } finally {
      setIsLoadingFile(false);
    }
  };





  const handleUploadToServer = async () => {
    if (!selectedFile) {
      alert('No file selected. Please select an image first.');
      return;
    }
    
    try {
      setIsUploading(true);
      
      // Use the stored file directly
      const result = await api.uploadImage(selectedFile);
      
      if (result.success) {
        console.log('Upload successful! R2 URL:', result.url);
        console.log('Updating preview and calling onImageSelect...');
        onImageSelect(result.url);
        setPreviewUrl(result.url);
        console.log('Preview URL updated to:', result.url);
        
        // Clear the selected file since it's now uploaded
        setSelectedFile(null);
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    onImageSelect('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Image Preview */}
      {previewUrl && (
        <div style={{ 
          position: 'relative', 
          marginBottom: '1rem',
          borderRadius: '0.5rem',
          overflow: 'hidden',
          border: '2px solid #e5e7eb'
        }}>
          <img 
            src={previewUrl} 
            alt="Product preview" 
            style={{ 
              width: '100%', 
              height: '200px', 
              objectFit: 'cover',
              display: 'block'
            }} 
          />
          <button
            onClick={removeImage}
            style={{
              position: 'absolute',
              top: '0.5rem',
              right: '0.5rem',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* File Input and Upload */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {/* File Input */}
        <div style={{
          flex: 1,
          minWidth: '140px'
        }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px dashed #d1d5db',
              borderRadius: '0.5rem',
              backgroundColor: '#f3f4f6',
              cursor: 'pointer'
            }}
            disabled={isLoadingFile}
          />
        </div>



        {/* Upload Button */}
        {selectedFile && (
          <button
            type="button"
            onClick={handleUploadToServer}
            disabled={isUploading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              backgroundColor: isUploading ? '#9ca3af' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s ease',
              minWidth: '140px'
            }}
            onMouseEnter={(e) => {
              if (!isUploading) {
                e.currentTarget.style.backgroundColor = '#059669';
              }
            }}
            onMouseLeave={(e) => {
              if (!isUploading) {
                e.currentTarget.style.backgroundColor = '#10b981';
              }
            }}
          >
            <ImageIcon size={20} />
            {isUploading ? 'Uploading...' : 'Upload Image'}
          </button>
        )}
      </div>

      {/* File input is now visible and integrated above */}





      {/* Upload Progress */}
      {isUploading && (
        <div style={{
          marginTop: '1rem',
          padding: '1rem',
          backgroundColor: '#f3f4f6',
          borderRadius: '0.5rem',
          border: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid #e5e7eb',
              borderTop: '2px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            Uploading image...
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
